import { useEffect, useState, type FormEvent } from 'react';
import { Building2, Plus } from 'lucide-react';
import { isApiError } from '../../lib/api';
import { createAccount, listAccounts, setAccountDisabled, type AccountRow } from '../../lib/accounts-api';
import {
  Button,
  Badge,
  Skeleton,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../components/ui';
import styles from './admin.module.css';
import { confirmDialog } from '../../stores/confirm';

/** The well-known id migration 0057 gives the backfilled account. */
const DEFAULT_ACCOUNT_ID = '00000000-0000-4000-8000-000000000001';

// Tenants (super-admin only). Each account owns its sites and agents; an
// account-admin created inside it manages them without seeing anyone else's.
// Disabling an account blocks its agents from signing in.
export function AdminAccounts() {
  const [list, setList] = useState<AccountRow[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setList(await listAccounts());
    } catch {
      setError('load-failed');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  function handleNameChange(val: string) {
    setName(val);
    if (!slugManuallyEdited) {
      const generated = val
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      setSlug(generated);
    }
  }

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await createAccount(name, slug);
      setName('');
      setSlug('');
      setSlugManuallyEdited(false);
      setModalOpen(false);
      await load();
    } catch (err) {
      setError(isApiError(err) && err.status === 409 ? 'That slug is already taken.' : 'Failed to create account.');
    } finally {
      setBusy(false);
    }
  }

  async function toggle(a: AccountRow) {
    const disabling = !a.disabledAt;
    if (
      disabling &&
      !(await confirmDialog({
        title: `Disable ${a.name}?`,
        message: 'Its agents can no longer sign in, and open sessions end within 15 minutes.',
        confirmLabel: 'Disable account',
      }))
    )
      return;
    try {
      await setAccountDisabled(a.id, disabling);
      await load();
    } catch {
      setError('update-failed');
    }
  }

  return (
    <section className={styles.section}>
      {/* Header with Title and Create Action on Right */}
      <div className={styles.headerRow}>
        <div className={styles.headerTitles}>
          <div className={styles.kicker}>
            <Building2 size={13} />
            <span>Workspace Scopes</span>
          </div>
          <h1 className={styles.title}>Customer accounts.</h1>
          <p className={styles.subtitle}>
            Multi-tenant customer accounts for isolating sites, embed tokens, and support agents.
          </p>
        </div>
        <Button onClick={() => setModalOpen(true)} className="gap-2 h-10 px-4">
          <Plus size={16} />
          <span>Create Account</span>
        </Button>
      </div>

      {error && !modalOpen && <p className={styles.error} role="alert">{error}</p>}

      {/* Create Account Modal Dialog */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className={styles.adminModalContent}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-semibold">
              <Building2 size={18} className="text-primary" />
              <span>Create Customer Account</span>
            </DialogTitle>
            <DialogDescription className="text-xs">
              Set up a new isolated customer account. Each account owns its sites and agents.
            </DialogDescription>
          </DialogHeader>

          <form className={styles.modalForm} onSubmit={handleCreate}>
            <div className={styles.field}>
              <label htmlFor="acc-name" className={styles.label}>Account Name</label>
              <input
                id="acc-name"
                className={styles.input}
                required
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="e.g. Acme Corporation"
                autoFocus
              />
              <span className={styles.hint}>Human-friendly company or organization name</span>
            </div>

            <div className={styles.field}>
              <label htmlFor="acc-slug" className={styles.label}>Account Slug (URL identifier)</label>
              <input
                id="acc-slug"
                className={styles.input}
                required
                pattern="[a-z0-9-]+"
                value={slug}
                onChange={(e) => {
                  setSlugManuallyEdited(true);
                  setSlug(e.target.value);
                }}
                placeholder="e.g. acme-corporation"
                style={{ fontFamily: 'var(--inic-font-mono, monospace)' }}
              />
              <span className={styles.hint}>
                Only lowercase letters, numbers, and hyphens (e.g. webgenia).
              </span>
            </div>

            {error && <p className={styles.error} role="alert">{error}</p>}

            <div className={styles.modalFooter}>
              <Button type="button" variant="outline" size="sm" onClick={() => setModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={busy}>
                {busy ? 'Creating…' : 'Create account →'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Accounts Table */}
      <div className={styles.tableWrap}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead className="w-[80px]">Sites</TableHead>
              <TableHead className="w-[80px]">Agents</TableHead>
              <TableHead className="w-[120px]">Created</TableHead>
              <TableHead className="w-[100px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton className="h-6 w-32" /></TableCell>
                  <TableCell><Skeleton className="h-6 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-6 w-8" /></TableCell>
                  <TableCell><Skeleton className="h-6 w-8" /></TableCell>
                  <TableCell><Skeleton className="h-6 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-6 w-16 ml-auto" /></TableCell>
                </TableRow>
              ))
            ) : list.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center text-xs text-muted-foreground">
                  No accounts found.
                </TableCell>
              </TableRow>
            ) : (
              list.map((a) => (
                <TableRow key={a.id} className="hover:bg-muted/40 transition-colors">
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-xs text-foreground">{a.name}</span>
                      {a.disabledAt ? (
                        <Badge variant="outline" className="text-[10px] text-muted-foreground">disabled</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] text-emerald-500 border-emerald-500/20 bg-emerald-500/10">
                          active
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{a.slug}</TableCell>
                  <TableCell className="font-mono text-xs">{a.siteCount}</TableCell>
                  <TableCell className="font-mono text-xs">{a.agentCount}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {new Date(a.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">
                    {a.id === DEFAULT_ACCOUNT_ID ? null : a.disabledAt ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs px-2.5"
                        onClick={() => void toggle(a)}
                      >
                        Enable
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs px-2 text-destructive hover:bg-destructive/10"
                        onClick={() => void toggle(a)}
                      >
                        Disable
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}
